import { createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Plug } from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Switch } from "@fleet/ui";
import { api, createApiQuery } from "@/lib/api";
import { type components, queryKeys } from "@cartero/shared";
import type { ConnectAccountInput } from "../api";
import { useConnectAccount, useMailOAuthProviders, useStartMailOAuth } from "../hooks";
import { Field, MailInput } from "./field";

/**
 * One-click OAuth connect (Gmail / Outlook). Only renders the buttons for
 * providers the server actually has credentials for; without any, the manual
 * IMAP/SMTP form below stands alone (self-host posture).
 */
function OAuthConnectButtons() {
  const { t } = useI18n();
  const providers = useMailOAuthProviders();
  const start = useStartMailOAuth();

  const any = () => Boolean(providers.data?.gmail || providers.data?.outlook);

  return (
    <Show when={any()}>
      <div class="flex flex-col gap-3" data-testid="connect-oauth-section">
        <div class="flex flex-wrap gap-2">
          <Show when={providers.data?.gmail}>
            <Button
              type="button"
              variant="outline"
              class="gap-1.5"
              disabled={start.isPending}
              onClick={() => start.mutate("gmail")}
              data-testid="connect-oauth-gmail"
            >
              {t(msg`Connect Gmail`)}
            </Button>
          </Show>
          <Show when={providers.data?.outlook}>
            <Button
              type="button"
              variant="outline"
              class="gap-1.5"
              disabled={start.isPending}
              onClick={() => start.mutate("outlook")}
              data-testid="connect-oauth-outlook"
            >
              {t(msg`Connect Outlook`)}
            </Button>
          </Show>
        </div>
        <Show when={start.isError}>
          <p class="text-sm text-destructive" role="alert" data-testid="connect-oauth-error">
            {start.error?.message}
          </p>
        </Show>
        <div class="flex items-center gap-3 text-xs text-muted-foreground">
          <span class="h-px flex-1 bg-border" aria-hidden="true" />
          {t(msg`or connect with IMAP/SMTP`)}
          <span class="h-px flex-1 bg-border" aria-hidden="true" />
        </div>
      </div>
    </Show>
  );
}

export function ConnectAccountForm(props: { onConnected: () => void }) {
  const { t } = useI18n();
  const connect = useConnectAccount();

  // Team mailboxes: when the user belongs to organizations, the account can be
  // bound to one so every member shares it (server enforces owner/admin).
  const orgs = createApiQuery<
    components["schemas"]["OrganizationDTO"][] | null,
    components["schemas"]["ErrorModel"]
  >(
    () => queryKeys.auth.organizations(),
    async () => api.GET("/api/org/listOrganizations", {}),
  );

  const [email, setEmail] = createSignal("");
  const [displayName, setDisplayName] = createSignal("");
  const [imapHost, setImapHost] = createSignal("");
  const [imapPort, setImapPort] = createSignal("993");
  const [imapTls, setImapTls] = createSignal(true);
  const [smtpHost, setSmtpHost] = createSignal("");
  const [smtpPort, setSmtpPort] = createSignal("465");
  const [smtpTls, setSmtpTls] = createSignal(true);
  const [username, setUsername] = createSignal("");
  const [password, setPassword] = createSignal("");
  const [orgId, setOrgId] = createSignal("");
  const [error, setError] = createSignal<string | null>(null);

  const orgOptions = () => [
    { value: "", label: t(msg`Personal (only you)`) },
    // P23 / CC-60 · I2: `.data` only on `isSuccess`.
    ...(orgs.isSuccess ? (orgs.data ?? []) : []).map((o) => ({ value: o.id, label: o.name })),
  ];

  const submit = (e: Event) => {
    e.preventDefault();
    setError(null);
    const body: ConnectAccountInput = {
      email: email().trim(),
      displayName: displayName().trim() || undefined,
      imapHost: imapHost().trim(),
      imapPort: Number.parseInt(imapPort(), 10) || 993,
      imapTls: imapTls(),
      smtpHost: smtpHost().trim(),
      smtpPort: Number.parseInt(smtpPort(), 10) || 465,
      smtpTls: smtpTls(),
      username: username().trim() || undefined,
      password: password(),
      orgId: orgId() || undefined,
    };
    connect.mutate(body, {
      onSuccess: () => props.onConnected(),
      onError: (err) => setError(err.message),
    });
  };

  return (
    <form class="flex flex-col gap-3" onSubmit={submit} data-testid="connect-account-form">
      <OAuthConnectButtons />
      <div class="grid gap-3 sm:grid-cols-2">
        <Field label={t(msg`Email address`)}>
          <MailInput
            type="email"
            required
            value={email()}
            onInput={(e) => setEmail(e.currentTarget.value)}
            placeholder={t(msg`you@example.com`)}
            data-testid="connect-email"
          />
        </Field>
        <Field label={t(msg`Display name`)}>
          <MailInput
            value={displayName()}
            onInput={(e) => setDisplayName(e.currentTarget.value)}
            data-testid="connect-display-name"
          />
        </Field>
      </div>

      <div class="grid gap-3 sm:grid-cols-[1fr_7rem_auto] sm:items-end">
        <Field label={t(msg`IMAP host`)}>
          <MailInput
            required
            value={imapHost()}
            onInput={(e) => setImapHost(e.currentTarget.value)}
            placeholder="imap.example.com"
            data-testid="connect-imap-host"
          />
        </Field>
        <Field label={t(msg`IMAP port`)}>
          <MailInput
            inputmode="numeric"
            value={imapPort()}
            onInput={(e) => setImapPort(e.currentTarget.value)}
            data-testid="connect-imap-port"
          />
        </Field>
        <label class="flex items-center gap-2 pb-2 text-xs text-muted-foreground">
          <Switch
            checked={imapTls()}
            onCheckedChange={setImapTls}
            aria-label={t(msg`IMAP TLS`)}
            data-testid="connect-imap-tls"
          />
          {t(msg`TLS`)}
        </label>
      </div>

      <div class="grid gap-3 sm:grid-cols-[1fr_7rem_auto] sm:items-end">
        <Field label={t(msg`SMTP host`)}>
          <MailInput
            required
            value={smtpHost()}
            onInput={(e) => setSmtpHost(e.currentTarget.value)}
            placeholder="smtp.example.com"
            data-testid="connect-smtp-host"
          />
        </Field>
        <Field label={t(msg`SMTP port`)}>
          <MailInput
            inputmode="numeric"
            value={smtpPort()}
            onInput={(e) => setSmtpPort(e.currentTarget.value)}
            data-testid="connect-smtp-port"
          />
        </Field>
        <label class="flex items-center gap-2 pb-2 text-xs text-muted-foreground">
          <Switch
            checked={smtpTls()}
            onCheckedChange={setSmtpTls}
            aria-label={t(msg`SMTP TLS`)}
            data-testid="connect-smtp-tls"
          />
          {t(msg`TLS`)}
        </label>
      </div>

      <div class="grid gap-3 sm:grid-cols-2">
        <Field label={t(msg`Username`)} hint={t(msg`Defaults to your email address.`)}>
          <MailInput
            value={username()}
            onInput={(e) => setUsername(e.currentTarget.value)}
            data-testid="connect-username"
            autocomplete="username"
          />
        </Field>
        <Field label={t(msg`Password`)}>
          <MailInput
            type="password"
            required
            value={password()}
            onInput={(e) => setPassword(e.currentTarget.value)}
            data-testid="connect-password"
            autocomplete="current-password"
          />
        </Field>
      </div>

      <Show when={orgs.isSuccess && (orgs.data?.length ?? 0) > 0}>
        <div data-testid="connect-org-section">
          <Field
            label={t(msg`Use as`)}
            hint={t(msg`A team mailbox is shared with every member of the organization.`)}
          >
            <Select<string>
              aria-label={t(msg`Use as`)}
              value={orgId()}
              onChange={(v) => setOrgId(v)}
              options={orgOptions()}
            />
          </Field>
        </div>
      </Show>

      <Show when={error()}>
        <p class="text-sm text-destructive" role="alert" data-testid="connect-error">
          {error()}
        </p>
      </Show>

      <div>
        <Button
          type="submit"
          class="gap-1.5"
          disabled={connect.isPending}
          data-testid="connect-submit"
        >
          <Plug class="h-4 w-4" />
          {connect.isPending ? t(msg`Verifying…`) : t(msg`Connect account`)}
        </Button>
      </div>
    </form>
  );
}
